import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useBreadcrumbs } from '../contexts/BreadcrumbsContext';
import AdminUsers from '../components/Admin/AdminUsers';
import AdminUserEdit from '../components/Admin/AdminUserEdit';
import AdminRoles from '../components/Admin/AdminRoles';
import AdminRoleDetail from '../components/Admin/AdminRoleDetail';
import AuditLogPage from '../components/AuditLog/AuditLogPage';
import Header from '../components/shared/TailwindComponents/Header';
import FilterBarWithTabs from '../components/shared/TailwindComponents/FilterBarWithTabs';
import { PiUploadLight } from 'react-icons/pi';
import { buildTabPath, legacyTabRedirect, resolveTabSlug } from '../components/shared/surface/tabPath';

const ADMIN_TABS = ['users', 'roles', 'audit-logs'] as const;
type AdminTabType = typeof ADMIN_TABS[number];

interface RouteParams {
  tab?: string;
  userId?: string;
  roleId?: string;
}

const AdminPageContent: React.FC<{ userInfo: any }> = ({ userInfo }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { tab: tabParam } = useParams<RouteParams>();

  /* The active tab is a PATH segment (`/admin/:tab?`), like Finance's
     `/finance/:tab?`: refresh, deep link and browser back/forward all resolve
     here. Unknown/absent → first tab. */
  const activeTab = resolveTabSlug(ADMIN_TABS, tabParam) as AdminTabType;
  const [inviteUserTrigger, setInviteUserTrigger] = useState(false);
  const [importUserTrigger, setImportUserTrigger] = useState(false);

  const handleCloseInviteModal = () => {
    setInviteUserTrigger(false);
  };

  useEffect(() => {
    const breadcrumbs = [
      { name: 'Admin', href: '/admin', current: true },
    ];
    setBreadcrumbs(breadcrumbs);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs]);

  /* Old `?tab=<x>` deep links/bookmarks → the new path form, once, preserving
     any other query params. Only when there is no path tab, so no loop. */
  useEffect(() => {
    const target = legacyTabRedirect('/admin', tabParam, location.search);
    if (target) history.replace(target);
  }, [tabParam, location.search, history]);

  const tabs = [
    { label: t('Users'), value: 'users' },
    { label: t('Roles'), value: 'roles' },
    { label: t('Audit logs'), value: 'audit-logs' },
  ];

  const handleTabClick = (tab: string) => {
    history.push(buildTabPath('/admin', tab));
  };

  const getHeaderActions = () => {
    switch (activeTab) {
      case 'users':
        return [
          {
            label: t('Import users'),
            icon: <PiUploadLight className="w-4 h-4" />,
            onClick: () => setImportUserTrigger(true),
            variant: 'outline' as const,
          },
          {
            label: t('Invite user'),
            onClick: () => {
              setInviteUserTrigger(!inviteUserTrigger);
            },
            variant: 'primary' as const,
          },
        ];
      case 'roles':
        return [
          {
            label: t('Create role'),
            onClick: () => {
              history.push('/admin/roles/create');
            },
            variant: 'primary' as const,
          },
        ];
      default:
        return [];
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'users':
        return <AdminUsers openInviteModal={inviteUserTrigger} onCloseInvite={handleCloseInviteModal} openImportModal={importUserTrigger} onCloseImport={() => setImportUserTrigger(false)} />;
      case 'roles':
        return <AdminRoles />;
      case 'audit-logs':
        return <AuditLogPage embedded />;
      default:
        return <AdminUsers openImportModal={importUserTrigger} onCloseImport={() => setImportUserTrigger(false)} />;
    }
  };

  return (
    <div className="flex flex-col h-full bg-background-light dark:bg-background-dark">
      {inviteUserTrigger && activeTab === 'users' ? (
        <div className="flex-1 overflow-y-auto">
          {renderContent()}
        </div>
      ) : (
        <>
          <Header actions={getHeaderActions()} />
          <FilterBarWithTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabClick={handleTabClick}
          />
          <div className="flex-1 overflow-y-auto">
            {renderContent()}
          </div>
        </>
      )}
    </div>
  );
};

const AdminPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const params = useParams<RouteParams>();

  useEffect(() => {
    if (!userInfo?.is_org_admin) {
      history.push('/not-found');
    }
  }, [userInfo?.is_org_admin, history]);

  if (!userInfo?.is_org_admin) {
    return null;
  }

  if (params.userId) {
    return <AdminUserEdit />;
  }

  if (params.roleId) {
    return <AdminRoleDetail />;
  }

  return <AdminPageContent userInfo={userInfo} />;
};

export default AdminPage;
